import React, { useMemo } from "react";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { IEvidence, LearningObjective, ProgramData, ProgramRole } from "@interfaces/Interfaces";
import LearningOutcomesCount from "./LearningOutcomesCount";
import { evidenceAtom } from "@state/State";
import * as ProgramUtils from "@utils/programUtils";
import * as NQPUtils from "@utils/nqpUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { userRolesSelector } from "@state/UserState";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import { subYears } from "date-fns";

type Props = {
  program?: ProgramData;
};

const NQPLearningOutcomesDisplay: React.FC<Props> = (props) => {
  const { currentProgramCycle } = useProgramCycles(props.program?.ID ?? "");

  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);
  const programRoles = allUserRoles.find((role) => role.programID === props.program?.ID);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  if (!props.program) {
    return null;
  }

  const role: ProgramRole | undefined = userRoles[0]
    ? props.program.Roles?.find((role) => role.Name === userRoles[0])
    : undefined;

  let learningObjectives: LearningObjective[];

  if (userRoles.length > 0) {
    learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(props.program, userRoles[0]);
  } else {
    learningObjectives = ProgramUtils.getAllLearningObjectivesForProgram(props.program);
  }

  let evidenceForProgram: IEvidence[] = [];
  let evidenceForCycle: IEvidence[] | undefined;

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    if (ProgramUtils.isNwasARC(props.program?.ID ?? "")) {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        if (!evidence.evidenceJSON) return false;

        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: props.program?.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: props.program?.["Evidence Definitions"] ?? [],
        });

        return (
          id === props.program?.ID &&
          (EvidenceUtils.evidenceHasLearningOutcomes(evidence) || programEvidenceType === "capabilities")
        );
      });

      evidenceForProgram = evidenceForProgram.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: props.program?.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: props.program?.["Evidence Definitions"] ?? [],
        });

        if (programEvidenceType === "capabilities") {
          return new Date(evidence.date) > subYears(new Date(), 2) && onHoldReset !== 1 && evidence.draft !== true;
        }

        return onHoldReset !== 1 && evidence.draft !== true;
      });
    } else if (ProgramUtils.isSPAPRP(props.program?.ID ?? "")) {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        if (!evidence.evidenceJSON) return false;

        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: props.program?.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: props.program?.["Evidence Definitions"] ?? [],
        });

        return (
          id === props.program?.ID &&
          (EvidenceUtils.evidenceHasLearningOutcomes(evidence) || programEvidenceType === "capabilities")
        );
      });

      if (currentProgramCycle) {
        evidenceForCycle = evidenceForProgram.filter(
          (item) => item.evidenceJSON?.programInfo?.programCycle === currentProgramCycle
        );
      }

      evidenceForCycle = evidenceForCycle?.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: props.program?.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: props.program?.["Evidence Definitions"] ?? [],
        });

        if (programEvidenceType === "capabilities") {
          return new Date(evidence.date) > subYears(new Date(), 2) && onHoldReset !== 1 && evidence.draft !== true;
        }

        return onHoldReset !== 1 && evidence.draft !== true;
      });
    } else {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return id === props.program?.ID && onHoldReset !== 1 && evidence.draft !== true;
      });
    }
  }

  const mandatoryLearningObjectives = useMemo(
    () => learningObjectives.filter((lo) => !lo.Optional),
    [learningObjectives]
  );
  const optionalLearningObjectives = useMemo(
    () => learningObjectives.filter((lo) => lo.Optional),
    [learningObjectives]
  );

  const useAutoLOs = props.program.AutoLOsForCalculations ?? false;
  const mandatoryCount = useMemo(
    () =>
      NQPUtils.getLearningOutcomesAchieved(
        mandatoryLearningObjectives,
        evidenceForCycle ?? evidenceForProgram,
        useAutoLOs
      ),
    [mandatoryLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const optionalCount = useMemo(
    () =>
      NQPUtils.getLearningOutcomesAchieved(
        optionalLearningObjectives,
        evidenceForCycle ?? evidenceForProgram,
        useAutoLOs
      ),
    [optionalLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );

  const mandatoryTotal = mandatoryLearningObjectives.length;
  const optionalTotal = optionalLearningObjectives.length;
  const mandatoryPercentage = mandatoryTotal > 0 ? Math.trunc((mandatoryCount / mandatoryTotal) * 100) : 0;
  const optionalPercentage = optionalTotal > 0 ? Math.trunc((optionalCount / optionalTotal) * 100) : 0;

  const showPartialCount = useMemo(
    () => NQPUtils.learningOutcomesHavePartialState(mandatoryLearningObjectives),
    [mandatoryLearningObjectives]
  );
  const mandatoryPartialCount = useMemo(
    () =>
      showPartialCount
        ? NQPUtils.getLearningOutcomesPartiallyMet(
            mandatoryLearningObjectives,
            evidenceForCycle ?? evidenceForProgram,
            useAutoLOs
          )
        : undefined,
    [showPartialCount, mandatoryLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const mandatoryPartialPercentage =
    mandatoryPartialCount !== undefined && mandatoryTotal > 0
      ? Math.trunc((mandatoryPartialCount / mandatoryTotal) * 100)
      : undefined;

  const showPartialCountOptional = useMemo(
    () => NQPUtils.learningOutcomesHavePartialState(optionalLearningObjectives),
    [optionalLearningObjectives]
  );
  const optionalPartialCount = useMemo(
    () =>
      showPartialCountOptional
        ? NQPUtils.getLearningOutcomesPartiallyMet(
            optionalLearningObjectives,
            evidenceForCycle ?? evidenceForProgram,
            useAutoLOs
          )
        : undefined,
    [showPartialCountOptional, optionalLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const optionalPartialPercentage =
    optionalPartialCount !== undefined && optionalTotal > 0
      ? Math.trunc((optionalPartialCount / optionalTotal) * 100)
      : undefined;

  return (
    <div className="mt-3">
      {mandatoryLearningObjectives.length > 0 && (
        <LearningOutcomesCount
          count={mandatoryCount}
          total={mandatoryTotal}
          percentage={mandatoryPercentage}
          collapse
          showExplanatoryText={role?.LOsCardExplanatoryText !== undefined}
          explanatoryText={role?.LOsCardExplanatoryText}
          partialCount={mandatoryPartialCount}
          partialPercentage={mandatoryPartialPercentage}
        />
      )}
      {optionalLearningObjectives.length > 0 && (
        <LearningOutcomesCount
          count={optionalCount}
          total={optionalTotal}
          percentage={optionalPercentage}
          showExplanatoryText={role?.LOsCardExplanatoryText !== undefined}
          explanatoryText={"of optional"}
          collapse
          partialCount={optionalPartialCount}
          partialPercentage={optionalPartialPercentage}
        />
      )}
    </div>
  );
};

export default NQPLearningOutcomesDisplay;
